/*
  legal-growth.js — lead tracking + Legal Demand Router.
  Loaded once from layout/theme.liquid (defer). No dependencies.

  Event taxonomy (sent to GA4 via gtag when present, and to Shopify customer events):
    contact_click   — tap on tel: / WhatsApp link. A click, NOT a lead.
    form_attempt    — consultation form submitted by the browser. NOT a lead.
    form_received   — browser-confirmed submission: the same tab submitted the consultation
                      form AND Shopify then rendered its success state (contact_posted=true).
                      Counted once per submission. It confirms Shopify accepted the post in the
                      browser; it does NOT confirm the notification email was delivered.
    router_select   — a choice inside the Legal Demand Router.
    service_reco_click — click on the article→service recommendation.
    service_hub_click  — click on a supporting article from a service landing page.
  Qualified inquiry / consultation booked / engagement signed are offline stages and are
  recorded by the office, never inferred in the browser.

  Privacy: payloads only contain fixed labels and the page path. Form field values,
  names, phone numbers and message text are never read or sent.
*/
(function () {
  'use strict';

  function track(name, params) {
    var payload = Object.assign({ page_path: location.pathname }, params || {});
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('alarbash_' + name, payload);
      }
    } catch (e) {}
  }
  window.alarbashTrack = track;

  function locationOf(el) {
    var host = el.closest('[data-track-location]');
    return host ? host.getAttribute('data-track-location') : 'content';
  }

  // Contact clicks (event delegation covers links inside page bodies too).
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest && event.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      var method = null;
      if (href.indexOf('tel:') === 0) method = 'phone';
      else if (/wa\.me\/|api\.whatsapp\.com|whatsapp:\/\//.test(href)) method = 'whatsapp';

      if (method) {
        track('contact_click', { method: method, location: locationOf(link) });
        return;
      }
      if (link.hasAttribute('data-reco-service')) {
        track('service_reco_click', {
          service: link.getAttribute('data-reco-service'),
          source: link.getAttribute('data-reco-source') || 'tags',
        });
      }
    },
    true
  );

  // Contact form: attempt + confirmed receipt. Only fixed labels are sent.
  // A real submit leaves a short-lived marker in this tab's sessionStorage. form_received is
  // counted only when that marker exists, so opening a success URL directly (or in a new tab,
  // or reloading it) never counts as a submission.
  var PENDING_KEY = 'alarbash_form_pending';
  var PENDING_MAX_AGE_MS = 30 * 60 * 1000;

  function cleanService(value) {
    return String(value || '').replace(/[^a-z_]/g, '').slice(0, 40) || 'general';
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form.contact-form__form')) return;
      var service = cleanService(form.getAttribute('data-service'));
      try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify({ service: service, t: Date.now() }));
      } catch (e) {}
      track('form_attempt', { form: 'consultation', service: service });
    },
    true
  );

  function confirmSubmission() {
    var success = document.querySelector('[data-form-received]');
    if (!success) return;
    // Shopify redirects a successful contact post back with contact_posted=true.
    if (!/(?:^|[?&])contact_posted=true(?:&|$)/.test(location.search.slice(1))) return;
    var pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
      sessionStorage.removeItem(PENDING_KEY);
    } catch (e) {
      pending = null;
    }
    if (!pending || typeof pending.t !== 'number') return;
    var age = Date.now() - pending.t;
    if (age < 0 || age > PENDING_MAX_AGE_MS) return;
    track('form_received', {
      form: 'consultation',
      service: cleanService(pending.service),
      confirmation: 'browser_success_state',
    });
  }

  // First-touch attribution for this browser session (no personal data, no cookies):
  // entry page + channel derived from the referrer's domain only. Used to fill the contact
  // form's "Entry page" / "Traffic source" fields so received inquiries can be attributed.
  var ATTR_KEY = 'alarbash_first_touch';
  function classifyReferrer(ref) {
    if (!ref) return 'direct';
    var host = '';
    try { host = new URL(ref).hostname.replace(/^www\./, ''); } catch (e) { return 'unknown'; }
    if (/(^|\.)molazem-alarbash\.com$/.test(host) || /myshopify\.com$/.test(host)) return 'internal';
    if (/(^|\.)(google|bing|yahoo|duckduckgo|yandex|ecosia|baidu)\./.test(host)) return 'organic_search';
    if (/(^|\.)(instagram|facebook|fb|tiktok|x|twitter|t|linkedin|lnkd|snapchat|youtube|wa|whatsapp)\./.test(host) || host === 't.co') return 'social';
    return 'referral';
  }
  function firstTouch() {
    try {
      var saved = sessionStorage.getItem(ATTR_KEY);
      if (saved) return JSON.parse(saved);
      var params = new URLSearchParams(location.search);
      var channel = params.get('gclid') || /cpc|paid/i.test(params.get('utm_medium') || '') ? 'paid' : classifyReferrer(document.referrer);
      if (channel === 'internal') channel = 'unknown';
      var data = { entry: location.pathname.slice(0, 200), channel: channel };
      sessionStorage.setItem(ATTR_KEY, JSON.stringify(data));
      return data;
    } catch (e) {
      return { entry: location.pathname.slice(0, 200), channel: 'unknown' };
    }
  }
  var touch = firstTouch();

  // Clicks on service-hub article links (service page → supporting article).
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest && event.target.closest('a[data-hub-article]');
      if (link) track('service_hub_click', { article: (link.getAttribute('href') || '').slice(0, 120) });
    },
    true
  );

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  onReady(function () {
    // Count a confirmed submission once (the marker is consumed, so a reload does not recount).
    confirmSubmission();

    document.querySelectorAll('form.contact-form__form').forEach(function (form) {
      var entry = form.querySelector('[data-entry-field]');
      var source = form.querySelector('[data-source-field]');
      if (entry) entry.value = touch.entry;
      if (source) source.value = touch.channel;
    });

    // Prefill the non-sensitive "requested service" field from ?service=<key>.
    var params = new URLSearchParams(location.search);
    var service = (params.get('service') || '').replace(/[^a-z_]/g, '').slice(0, 40);
    if (service) {
      document.querySelectorAll('form.contact-form__form').forEach(function (form) {
        var field = form.querySelector('[data-service-field]');
        var label = document.querySelector('[data-service-label="' + service + '"]');
        if (field) field.value = label ? label.textContent.trim() : service;
        form.setAttribute('data-service', service);
      });
    }

    document.querySelectorAll('[data-legal-router]').forEach(initRouter);
  });

  // Legal Demand Router — progressive enhancement. Without JS every option list is visible.
  function initRouter(root) {
    var choices = root.querySelectorAll('[data-router-choice]');
    var panels = root.querySelectorAll('[data-router-panel]');
    var status = root.querySelector('[data-router-status]');
    root.setAttribute('data-enhanced', '');

    function select(key, focusPanel) {
      choices.forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(btn.getAttribute('data-router-choice') === key));
      });
      var active = null;
      panels.forEach(function (panel) {
        var match = panel.getAttribute('data-router-panel') === key;
        panel.hidden = !match;
        if (match) active = panel;
      });
      if (active && status) status.textContent = active.getAttribute('aria-label') || '';
      if (active && focusPanel) {
        var heading = active.querySelector('[data-router-heading]');
        if (heading) heading.focus();
      }
    }

    panels.forEach(function (panel) {
      panel.hidden = true;
    });

    choices.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-router-choice');
        select(key, true);
        track('router_select', { step: 'category', choice: key });
      });
    });

    root.addEventListener('click', function (event) {
      var link = event.target.closest('[data-router-option]');
      if (link) track('router_select', { step: 'service', choice: link.getAttribute('data-router-option') });
    });
  }
})();
